// @ts-check
import axios from "axios";
import CommonHelper from "@/helpers/common.helper.js";

// The hotel's headline display offer is decided by the backend
// (OfferDisplayService.PopulateHotelDisplayOffer) and delivered on
// `hotel.displayOffer`. The frontend performs no display decision here.
const getDisplayOffer = (hotel) => hotel?.displayOffer ?? null;

// Comparison / search-results card contract. The backend owns the comparison
// display decision and emits `hotel.comparisonDisplayOffer` (price, nightlyPrice,
// isRefundable, isBreakfastIncluded, hasDirectBook, bookingMode, priceLabelKey).
// The card renders straight from it; there is no frontend fallback here.
const getComparisonDisplayOffer = (hotel) => {
  return hotel?.comparisonDisplayOffer ?? null;
};

// Whether the comparison card's CTA is direct booking ("nu boeken") vs a
// request ("reservering aanvragen"). The backend decides this and sets
// `bookingMode` on the comparison display offer; the frontend just reads it.
const isDirectBookingOffer = (offer) => {
  return offer?.bookingMode === "Direct";
};

// This method returns all hotels with offers
const getHotelsWithOffers = (hotels) => {
  let hotelsWithOffers = hotels.filter((hotel) => {
    return hotelHasOffers(hotel);
  });

  return hotelsWithOffers;
};

const getPrice = (offer) => {
  return getFormattedPrice(offer.price);
};

const getPricePerNight = (offer) => {
  if (offer) {
    const nightly = offer.nightlyPrice > 0 ? offer.nightlyPrice : offer.price;
    if (nightly) return getFormattedPrice(nightly);
  }

  return null;
};
const getBarePricePerNight = (offer) => {
  if (offer) {
    const nightly = offer.nightlyPrice > 0 ? offer.nightlyPrice : offer.price;
    if (nightly) return nightly;
  }

  return null;
};

// the method queries the API for available hotels given the user input
const getHotelPrices = async (
  hotelId,
  hotelName,
  locationPath,
  checkin,
  nights,
  adults,
  rooms
) => {
  const url = `${CommonHelper.getApiURL()}/hotel/getwithpricesbygeopath`;
  let progress = 0,
    hotelData;

  let searchData = {
    page: 1,
    isoLanguage: "nl",
    locationPath: locationPath,
    hotelName: hotelName,
    amount: 25,
    radius: -1,
    stars: [],
    adults: adults,
    rooms: rooms,
    refundable: true,
    checkin: checkin,
    nights: nights,
    reviewScore: [],
    facilities: [],
    feedbackId: null
  };

  // The API call is made until the reported progress is 100. This indicates the progress the api makes in fetching prices in the date range
  while (progress < 100) {
    let { data } = await axios.post(url, searchData);
    progress = data.progress;

    searchData.feedbackId = data.feedbackId;

    if (data.progress == 100) {
      hotelData = data.hotels.find((h) => h.hotelId == hotelId);
    } else {
      const waitTime = 1500;
      // Wait waitTime milliseconds before making the next call and check for the updated progress
      await sleep(waitTime);
    }
  }
  return hotelData;
};

// Sleep function to wait for a certain amount of time, used in the getHotelPrices method
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// DUPLICATE_CHECK: This function exists as formatPrice in common.helper.js. Consider refactoring to avoid duplication.
const getFormattedPrice = (price) => {
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR"
  }).format(price);
};

// Iterates over all suppliers and checks if there is at least one offer with a valid price
const hotelHasOffers = (hotel) => {
  let hasOffer = false;
  hotel.suppliers = hotel.suppliers.forEach((s) => {
    if (s.offers && s.offers.length) {
      s.offers.forEach((o) => {
        const hasValidPrice = o.price && o.price > 0;
        if (hasValidPrice) {
          hasOffer = true;
        }
      });
    }
  });

  return hasOffer;
};

// Resolve an i18n message by key against a locale-message object. Handles a
// fully-flat key, a flat group key ending in a dotted leaf (the app stores
// "recommended.pricing" as a single key with a nested object), and a nested
// path walk — so a backend key like "recommended.pricing.fixedRate" resolves
// regardless of how the messages are structured.
const resolveMessage = (messages, key) => {
  if (messages == null) return null;
  if (messages[key] != null) return messages[key];
  const lastDot = key.lastIndexOf(".");
  if (lastDot > 0) {
    const group = messages[key.slice(0, lastDot)];
    const leaf = key.slice(lastDot + 1);
    if (group != null && group[leaf] != null) return group[leaf];
  }
  return key
    .split(".")
    .reduce(
      (acc, part) => (acc != null && acc[part] != null ? acc[part] : null),
      messages
    );
};

const getOfferPricingText = (hotel, i18n) => {
  const key = hotel?.priceLabelKey;
  if (!key) return null;

  const locale =
    typeof i18n.locale === "string" ? i18n.locale : i18n.locale.value;
  const localeMessages = i18n.getLocaleMessage
    ? i18n.getLocaleMessage(locale)
    : i18n.messages[locale];
  const rt = i18n.getLocaleMessage
    ? (msg) =>
        typeof msg === "function"
          ? msg({ normalize: (parts) => parts.join("") })
          : msg
    : (msg) => msg;

  const resolved = resolveMessage(localeMessages, String(key));
  return resolved != null ? rt(resolved) : null;
};

const canBookOnBehalfOfOthers = (companyInfo) => {
  return companyInfo?.userSettings?.canBookOnBehalfOfOthers || false;
};
const canEditExpenses = (companyInfo) => {
  return companyInfo?.allowChangingExpenses || false;
};

export default {
  hotelHasOffers,
  getPrice,
  getPricePerNight,
  getBarePricePerNight,
  getHotelsWithOffers,
  getHotelPrices,
  getOfferPricingText,
  getDisplayOffer,
  getComparisonDisplayOffer,
  isDirectBookingOffer,
  canEditExpenses,
  canBookOnBehalfOfOthers
};
